import { useEffect, useRef, useState } from 'react'
import { RollVariant, ROLL_DIMENSIONS } from '@/types'
import { chopstick } from '@/assets/ui'
import { regularRolls, hotRolls } from '@/assets/rolls'
import { PIXELS_PER_UNIT } from '@/store/gameStore'

// === НАСТРОЙКИ (экспортируем для редактора) ===
export interface ChopsticksConfigType {
  roll: { width: number; height: number; top: number }
  stick1: { width: number; height: number; angle: number; top: number; offsetX: number }
  stick2: { width: number; height: number; angle: number; top: number; offsetX: number }
  amplitude: number // fallback pixel amplitude for demo mode
}

export const DEFAULT_CONFIG: ChopsticksConfigType = {
  roll: { width: 78, height: 78, top: 8 },
  stick1: { width: 452, height: 204, angle: 45, top: -150, offsetX: 106 },
  stick2: { width: 627, height: 174, angle: 32, top: -136, offsetX: 57 },
  amplitude: 85,
}

interface ChopsticksProps {
  isHot: boolean
  rollVariant: RollVariant
  onPositionChange: (position: number) => void
  config?: ChopsticksConfigType
  isDropping?: boolean
  isFirstRoll?: boolean

  // Server cycle params (when connected to backend).
  // If provided, the sin wave is driven by these instead of local timing.
  serverCycleStartedAt?: string | null
  // Client-local time (Date.now()) when the cycle was received.
  // Used for animation timing to avoid clock-skew with the server.
  serverCycleStartedAtClientTime?: number
  serverCyclePeriodMs?: number
  serverCyclePhaseShiftMs?: number
  serverCycleAmplitude?: number   // normalized units (backend space)
  serverCycleOriginX?: number     // normalized units
}

// Max amplitude for first roll so it stays above the plate
const FIRST_ROLL_MAX_AMPLITUDE = 58

export default function Chopsticks({
  isHot,
  rollVariant,
  onPositionChange,
  config,
  isDropping = false,
  isFirstRoll = false,
  serverCycleStartedAt,
  serverCycleStartedAtClientTime,
  serverCyclePeriodMs,
  serverCyclePhaseShiftMs,
  serverCycleAmplitude,
  serverCycleOriginX,
}: ChopsticksProps) {
  const animationRef = useRef<number | null>(null)
  const [offsetX, setOffsetX] = useState(0)
  const cfg = config || DEFAULT_CONFIG

  const useServerCycle = !!serverCycleStartedAt && !!serverCyclePeriodMs

  // Roll asset
  const rollAsset = isHot
    ? hotRolls[rollVariant >= 6 ? (rollVariant - 6) % hotRolls.length : 0]
    : regularRolls[(rollVariant - 1) % regularRolls.length]

  // Animation loop — uses server cycle params when available, otherwise
  // falls back to local sin wave (demo mode).
  useEffect(() => {
    if (isDropping) return

    let startTime: number
    let periodMs: number
    let phaseShiftMs: number
    let amplitudePx: number
    let originPx: number

    if (useServerCycle) {
      // Server-synced mode: use the CLIENT-LOCAL time when the cycle was
      // received as the baseline. This avoids clock-skew with the server.
      // (Equivalent to: phase=0 at client receive moment.)
      startTime = serverCycleStartedAtClientTime || Date.now()
      periodMs = serverCyclePeriodMs!
      phaseShiftMs = serverCyclePhaseShiftMs ?? 0
      amplitudePx = (serverCycleAmplitude ?? 0.42) * PIXELS_PER_UNIT
      originPx = (serverCycleOriginX ?? 0) * PIXELS_PER_UNIT
    } else {
      // Demo/local mode: independent sin wave
      startTime = Date.now()
      const speed = isHot ? 1.3 : 1.8 // seconds per cycle
      periodMs = speed * 1000
      phaseShiftMs = 0
      amplitudePx = isHot ? cfg.amplitude * 1.2 : cfg.amplitude
      originPx = 0
    }

    if (isFirstRoll) {
      amplitudePx = Math.min(amplitudePx, FIRST_ROLL_MAX_AMPLITUDE)
    }

    const animate = () => {
      const elapsedMs = Date.now() - startTime
      // Exact same formula as backend: sin(2π * (elapsed + phase) / period)
      const angle = 2 * Math.PI * ((elapsedMs + phaseShiftMs) / periodMs)
      const newOffset = originPx + Math.sin(angle) * amplitudePx

      setOffsetX(newOffset)
      onPositionChange(newOffset)
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [
    isHot, onPositionChange, cfg.amplitude, isDropping, isFirstRoll,
    useServerCycle, serverCycleStartedAt, serverCycleStartedAtClientTime,
    serverCyclePeriodMs, serverCyclePhaseShiftMs, serverCycleAmplitude, serverCycleOriginX,
  ])

  // Drop animation offsets
  const dropAngleOffset = isDropping ? 15 : 0
  const dropYOffset = isDropping ? -50 : 0
  const rollOpacity = isDropping ? 0 : 1

  return (
    <div
      className="absolute inset-x-0 top-0 pointer-events-none"
      style={{
        transform: `translateX(${offsetX}px) translateZ(0)`,
        willChange: 'transform',
      }}
    >
      {/* ПАЛОЧКА 2 (задняя) */}
      <div
        className="absolute"
        style={{
          width: `${cfg.stick2.width}px`,
          height: `${cfg.stick2.height}px`,
          top: `${cfg.stick2.top + dropYOffset}px`,
          left: `calc(50% + ${cfg.stick2.offsetX}px)`,
          transform: `translateX(-50%) rotate(${cfg.stick2.angle - dropAngleOffset}deg)`,
          transformOrigin: 'center center',
          zIndex: 40,
          transition: isDropping ? 'all 300ms ease-out' : 'none',
        }}
      >
        <img src={chopstick} alt="" className="w-full h-full object-contain" style={{ transform: 'rotate(-90deg)' }} draggable={false} />
      </div>

      {/* РОЛЛ */}
      <div
        className="absolute"
        style={{
          width: `${ROLL_DIMENSIONS[rollVariant].width}px`,
          height: `${ROLL_DIMENSIONS[rollVariant].height}px`,
          top: `${cfg.roll.top}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 45,
          opacity: rollOpacity,
          transition: 'opacity 100ms ease-out',
        }}
      >
        {isHot && (
          <>
            <div
              className="absolute inset-0 animate-fire-glow"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(255,100,0,0.8) 0%, rgba(255,50,0,0.4) 35%, transparent 60%)',
                transform: 'scale(1.8)',
                borderRadius: '50%',
              }}
            />
            <div
              className="absolute inset-0 animate-fire-glow-delayed"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(255,220,50,0.9) 0%, rgba(255,150,0,0.5) 30%, transparent 55%)',
                transform: 'scale(1.3)',
                borderRadius: '50%',
              }}
            />
          </>
        )}
        <img
          src={rollAsset.src}
          alt={rollAsset.name}
          className="w-full h-full object-contain"
          style={{
            filter: isHot ? 'drop-shadow(0 0 8px rgba(255,100,0,0.6))' : 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
            transform: 'translateZ(0)',
            WebkitBackfaceVisibility: 'hidden',
          }}
          draggable={false}
        />
      </div>

      {/* ПАЛОЧКА 1 (передняя) */}
      <div
        className="absolute"
        style={{
          width: `${cfg.stick1.width}px`,
          height: `${cfg.stick1.height}px`,
          top: `${cfg.stick1.top + dropYOffset}px`,
          left: `calc(50% + ${cfg.stick1.offsetX}px)`,
          transform: `translateX(-50%) rotate(${cfg.stick1.angle + dropAngleOffset}deg)`,
          transformOrigin: 'center center',
          zIndex: 50,
          transition: isDropping ? 'all 300ms ease-out' : 'none',
        }}
      >
        <img src={chopstick} alt="" className="w-full h-full object-contain" style={{ transform: 'rotate(-90deg)' }} draggable={false} />
      </div>

      {isHot && !isDropping && (
        <div
          className="absolute px-4 py-1.5 bg-gradient-to-r from-red-600 to-orange-500 rounded-full text-sm font-bold text-white animate-pulse shadow-lg whitespace-nowrap"
          style={{
            fontFamily: "'Big Shoulders Display', sans-serif",
            top: `${cfg.roll.top - 30}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 55,
          }}
        >
          🔥 HOT ROLL
        </div>
      )}
    </div>
  )
}
